import { i18n } from "../i18n"
import { apiRequest } from "./apiClient"

// The backend's invitation errors are English strings; map the known cases to
// localized copy so a Norwegian invitee doesn't hit an English dead-end on the
// accept screen.
const ACCEPT_ERROR_PATTERNS: Array<[RegExp, string]> = [
  [/already been used/i, "error_used"],
  [/expired/i, "error_expired"],
  [/already a member|already accepted|already declined|invitation already/i, "error_already_handled"],
  [/not found|invalid invitation/i, "error_invalid"],
]

function localizeAcceptError(error: unknown): string {
  const t = i18n.global.t
  const message = error instanceof Error ? error.message : ""
  for (const [pattern, key] of ACCEPT_ERROR_PATTERNS) {
    if (pattern.test(message)) return t(`magic_link.${key}`)
  }
  return t("magic_link.error_message")
}

interface AcceptResult {
  success: boolean
  requiresAuth?: boolean
  invitedEmail?: string
  householdName?: string
  errorTitle?: string
  errorMessage?: string
}

interface CurrentUser {
  uid: string
  email: string | null
  displayName: string | null
  photoURL: string | null
}

/**
 * Parse magic link parameters from URL
 */
export function getMagicLinkParams(): { token: string; id: string } | null {
  const urlParams = new URLSearchParams(window.location.search)
  const token = urlParams.get("token")
  const id = urlParams.get("id")

  if (!token || !id) {
    return null
  }

  return { token, id }
}

/**
 * Check if current URL is a magic link
 */
export function isMagicLinkUrl(): boolean {
  return window.location.pathname === "/accept-invitation" && getMagicLinkParams() !== null
}

/**
 * Clear magic link parameters from URL without page reload
 */
export function clearMagicLinkParams(): void {
  const url = new URL(window.location.href)
  url.searchParams.delete("token")
  url.searchParams.delete("id")
  url.pathname = "/"
  window.history.replaceState({}, document.title, url.toString())
}

/**
 * Validate and accept an invitation using a magic link token.
 * The server does all the validation (token, expiry, email match) and returns
 * a friendly error if anything fails.
 */
export async function acceptInvitationWithToken(
  token: string,
  invitationId: string,
  user: CurrentUser | null,
): Promise<AcceptResult> {
  // Sign-in is required so the API can match the invitee by email.
  if (!user) {
    return { success: false, requiresAuth: true }
  }
  try {
    const result = await apiRequest<{ success: boolean; householdName?: string }>(
      `/api/invitations/${invitationId}/accept`,
      { method: "POST", body: { token } },
    )
    return { success: true, householdName: result.householdName }
  } catch (error) {
    return {
      success: false,
      errorTitle: i18n.global.t("magic_link.error_accept_title"),
      errorMessage: localizeAcceptError(error),
    }
  }
}
